import { Bell, Camera, Car, ChevronRight, Clock, FileText, LogOut, Moon, ShieldCheck, Smartphone, Star, Sun, SunMoon, Volume2, Wallet } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ACCESS,
  dateOnly,
  DOCUMENT_STATUS,
  DocumentFileInput,
  DocumentFileModal,
  Empty,
  Field,
  Loadable,
  money,
  Notice,
  prepareDocumentFile,
  REQUIREMENT_STATE,
  requirementDetail,
  requirementNeedsAction,
  StatusBadge,
  todayInput,
  useAction,
  useAsync,
  useConfirm,
  VEHICLE_STATUS,
  type DocumentRequirement,
} from '@despachador/ui';
import { api } from '../lib/api';
import { saveTheme, storedTheme, type ThemeChoice } from '../lib/theme';
import { useDriver } from '../store';
import { ChoiceGroup, MenuList, SubPage } from '../components';
import type { AccessEvaluation } from '../types';

interface DocumentRow {
  id: string;
  document_type_name: string;
  number: string | null;
  expires_at: string | null;
  status: string;
  file_id: string | null;
}
interface AccountData {
  evaluation: AccessEvaluation;
  subscription: { paid_until: string | null; plan_name: string; plan_kind: string; exempt_until: string | null } | null;
  payments: Array<{ id: string; paid_on: string; reference: string | null; voided_at: string | null; amount_minor: number; currency: string }>;
}
interface VehicleData {
  plate: string;
  status: string;
  vehicle_type_name: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  color: string | null;
  capacity: number;
  documents: DocumentRow[];
  requirements: DocumentRequirement[];
}
interface DocumentsData {
  documents: DocumentRow[];
  compliance: { driver: { valid: boolean } };
  requirements: { driver: DocumentRequirement[] };
  documentTypes: Array<{ id: string; name: string; applies_to: string; active: boolean; requires_expiry: boolean }>;
}

/** "2030-01-01" or a timestamp -> "01/01/2030" (the design's dd/mm/aaaa, in mono). */
function dmy(iso: string | null): string {
  if (!iso) return '—';
  // a plain date is read at noon, so no time zone moves it to the day before
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

/** "+573000000001" -> "+57 300 000 0001"; other numbers as they are. */
const phoneText = (p: string | null) => (p ? p.replace(/^\+57(\d{3})(\d{3})(\d{4})$/, '+57 $1 $2 $3') : null);

/** "Carlos Pérez" -> "CP". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '')).toUpperCase() || '?';
}

const THEMES: Array<{ value: ThemeChoice; label: string; icon: typeof Sun }> = [
  { value: 'system', label: 'Sistema', icon: Smartphone },
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Oscuro', icon: Moon },
];

/** Light / dark / the phone's (DRV-010). */
function ThemePicker() {
  const [choice, setChoice] = useState<ThemeChoice>(storedTheme);
  return (
    <div className="theme-row">
      <div className="theme-head">
        <SunMoon size={22} aria-hidden />
        <span id="theme-label" className="menu-name">
          Tema
        </span>
      </div>
      <ChoiceGroup
        variant="segmented"
        labelledBy="theme-label"
        describedBy="theme-help"
        value={choice}
        onChange={(v) => {
          setChoice(v);
          saveTheme(v);
        }}
        options={THEMES.map((t) => {
          const Icon = t.icon;
          return {
            value: t.value,
            label: (
              <>
                <Icon size={16} aria-hidden /> {t.label}
              </>
            ),
          };
        })}
      />
      <p id="theme-help" className="hint">
        «Sistema» sigue el modo de tu teléfono. La alerta de servicio siempre es oscura.
      </p>
    </div>
  );
}

/**
 * The account as an index: each row says its current state, so most visits end here without
 * opening anything. Signing out is one tap away, at the end.
 */
export function Account({ onLogout }: { onLogout: () => void }) {
  const { me, presence, active } = useDriver();
  const confirm = useConfirm();
  if (!me) return null;
  const p = me.profile;
  const access = me.dashboard.access;
  const docsOk = p.compliance.driver.valid && (!p.compliance.vehicle || p.compliance.vehicle.valid);
  const expiring = [...(p.requirements?.driver ?? []), ...(p.requirements?.vehicle ?? [])].some((r) => r.state === 'EXPIRING');
  const logout = async () => {
    const working = !!active || presence === 'ONLINE' || presence === 'BUSY';
    const ok = await confirm({
      title: 'Cerrar sesión',
      message: active
        ? 'Tienes un servicio en curso. Si cierras sesión no podrás actualizarlo desde este teléfono.'
        : working
          ? 'Estás conectado: al cerrar sesión dejarás de recibir ofertas.'
          : '¿Quieres cerrar tu sesión en este teléfono?',
      danger: working,
      confirmLabel: 'Cerrar sesión',
    });
    if (ok) onLogout();
  };
  return (
    <div className="account">
      <header className="profile">
        <span className="avatar" aria-hidden>
          {initials(p.full_name)}
        </span>
        <div className="profile-main">
          <h1 className="profile-name">{p.full_name}</h1>
          <span className="profile-meta">{[p.code, phoneText(p.phone)].filter(Boolean).join('  ')}</span>
          {p.rating !== null && (
            <span className="profile-rating">
              <Star size={14} aria-hidden className="star" /> <b>{p.rating.toFixed(1)}</b> calificación
            </span>
          )}
        </div>
      </header>
      <MenuList
        label="Para trabajar"
        items={[
          { to: '/cuenta/cuota', icon: <Wallet size={22} aria-hidden />, name: 'Cuota', value: <StatusBadge status={access.status} map={ACCESS} /> },
          { to: '/cuenta/vehiculo', icon: <Car size={22} aria-hidden />, name: 'Vehículo', value: p.plate ? <span className="plate-tag">{p.plate}</span> : 'Sin asignar' },
          {
            to: '/cuenta/documentos',
            icon: <FileText size={22} aria-hidden />,
            name: 'Documentos',
            value: !docsOk ? (
              <span className="badge danger">
                <span className="dot" aria-hidden /> Revisar
              </span>
            ) : expiring ? (
              <span className="badge warn">
                <span className="dot" aria-hidden /> Por vencer
              </span>
            ) : (
              <span className="badge ok">
                <span className="dot" aria-hidden /> Vigentes
              </span>
            ),
          },
        ]}
      />
      <section className="menu-group">
        <h2 className="micro-title">App</h2>
        <ThemePicker />
        <nav className="menu-list" aria-label="App">
          <MenuRow to="/cuenta/alertas" icon={<Volume2 size={22} aria-hidden />} name="Sonido y alertas" />
          <MenuRow to="/cuenta/notificaciones" icon={<Bell size={22} aria-hidden />} name="Notificaciones" />
          <MenuRow to="/cuenta/seguridad" icon={<ShieldCheck size={22} aria-hidden />} name="Seguridad y sesiones" />
        </nav>
      </section>
      <div className="page-pad">
        <button type="button" className="btn logout block" onClick={() => void logout()}>
          <LogOut size={18} aria-hidden /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}

/** A row of the «App» group, built by hand because the theme picker sits in the same group. */
function MenuRow({ to, icon, name }: { to: string; icon: ReactNode; name: string }) {
  return (
    <Link to={to}>
      {icon}
      <span className="menu-name">{name}</span>
      <ChevronRight size={18} aria-hidden className="chev" />
    </Link>
  );
}

export function Quota() {
  const acc = useAsync(() => api.get<AccountData>('/driver/account'));
  return (
    <SubPage title="Cuota" aside={acc.data ? <StatusBadge status={acc.data.evaluation.status} map={ACCESS} /> : undefined} flush>
      <Loadable state={acc}>
        {(a) => (
          <>
            <section className="sheet">
              {a.subscription ? (
                <dl className="facts two">
                  <div>
                    <dt>Plan</dt>
                    <dd>{a.subscription.plan_name}</dd>
                  </div>
                  {a.subscription.plan_kind === 'EXEMPT' ? (
                    <div>
                      <dt>Exento hasta</dt>
                      <dd className="mono">{a.subscription.exempt_until ? dmy(a.subscription.exempt_until) : 'Sin fecha de fin'}</dd>
                    </div>
                  ) : (
                    <div>
                      <dt>Pagado hasta</dt>
                      <dd className="mono">{dmy(a.subscription.paid_until)}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="muted">No tienes un plan asignado.</p>
              )}
              <p className="sheet-note">{a.evaluation.reason}</p>
            </section>
            <section className="menu-group">
              <h2 className="micro-title">Pagos registrados</h2>
              {a.payments.length === 0 ? (
                <div className="page-pad">
                  <Empty>Aún no hay pagos registrados. Los registra la oficina cuando pagas.</Empty>
                </div>
              ) : (
                <ul className="row-list">
                  {a.payments.map((p) => (
                    <li key={p.id} className={p.voided_at ? 'payment voided' : 'payment'}>
                      <span>
                        {dateOnly(p.paid_on)} {p.voided_at && <span className="badge">Anulado</span>}
                        {p.reference && <span className="ref">ref. {p.reference}</span>}
                      </span>
                      <b className="mono">{money(p.amount_minor, p.currency)}</b>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </Loadable>
    </SubPage>
  );
}

function DocumentList({ items, kind }: { items: DocumentRow[]; kind: 'driver' | 'vehicle' }) {
  const [viewing, setViewing] = useState<DocumentRow | null>(null);
  return (
    <>
      <ul className="plain-list">
        {items.map((x) => (
          <li key={x.id} className="requirement">
            <span>
              <b>{x.document_type_name}</b>
              {x.number ? ` · ${x.number}` : ''}
              <br />
              <span className="muted small">{x.expires_at ? `Vence el ${dateOnly(x.expires_at)}` : 'Sin vencimiento'}</span>
              {x.file_id && (
                <button type="button" className="btn sm" onClick={() => setViewing(x)}>
                  Ver archivo
                </button>
              )}
            </span>
            <StatusBadge status={x.status} map={DOCUMENT_STATUS} />
          </li>
        ))}
      </ul>
      {viewing && (
        <DocumentFileModal
          title={viewing.document_type_name}
          fileName={viewing.document_type_name}
          load={() => api.blob(`/driver/documents/${kind}/${viewing.id}/file`)}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}

/** «Vence pronto» reads better than «Por vencer» next to a date (DRV-010). */
const REQUIREMENT_BADGE = { ...REQUIREMENT_STATE, EXPIRING: { label: 'Vence pronto', tone: 'warn' } };

/** One row per document type: its name and state, its date (or what the state means) and, when needed, what to do. */
function RequirementList({ items, onSend }: { items: DocumentRequirement[]; onSend?: (typeId: string) => void }) {
  return (
    <ul className="row-list">
      {items.map((r) => {
        const dated = (r.state === 'VALID' || r.state === 'EXPIRING') && r.expiresAt;
        return (
          <li key={r.documentTypeId} className="requirement">
            <div className="requirement-top">
              <b>{r.documentTypeName}</b>
              <StatusBadge status={r.state} map={REQUIREMENT_BADGE} />
            </div>
            <span className="requirement-detail">
              {r.state === 'REJECTED' && r.reviewNotes ? (
                <>
                  Lo rechazaron: «{r.reviewNotes}». Envía uno nuevo que corrija eso.
                  {!r.required && ' · opcional'}
                </>
              ) : dated ? (
                <>
                  Vence el <span className="mono">{dmy(r.expiresAt)}</span>
                  {!r.required && ' · opcional'}
                </>
              ) : (
                requirementDetail(r)
              )}
            </span>
            {dated && r.pendingDocumentId && (
              <span className="requirement-review">
                <Clock size={14} aria-hidden /> Renovación en revisión
              </span>
            )}
            {onSend && requirementNeedsAction(r) && (
              // 48 px across (screen 08): what blocks takes the primary action; what is only about to expire, the secondary
              <button type="button" className={`btn lg block${r.state === 'EXPIRING' ? '' : ' primary'}`} onClick={() => onSend(r.documentTypeId)}>
                <Camera size={18} aria-hidden /> {r.state === 'MISSING' ? 'Enviar foto' : 'Enviar foto nueva'}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Earlier submissions, folded: the requirement list already says what counts. */
function DocumentHistory({ items, kind }: { items: DocumentRow[]; kind: 'driver' | 'vehicle' }) {
  if (!items.length) return null;
  return (
    <details className="history">
      <summary>
        Historial de envíos <span className="mono">{items.length}</span>
      </summary>
      <DocumentList items={items} kind={kind} />
    </details>
  );
}

export function Vehicle() {
  const veh = useAsync(() => api.get<VehicleData | null>('/driver/vehicle'));
  // null means "no vehicle assigned", so loading/error are handled before the empty state
  const pending = veh.error || (veh.loading && !veh.data);
  const v = veh.data;
  return (
    <SubPage title="Vehículo" flush>
      {pending ? (
        <div className="page-pad">
          <Loadable state={veh}>{() => null}</Loadable>
        </div>
      ) : v ? (
        <>
          <section className="vehicle-head" aria-label="Placa y estado">
            <div className="vehicle-plate-row">
              <span className="plate-big">{v.plate}</span>
              <StatusBadge status={v.status} map={VEHICLE_STATUS} />
            </div>
            <p className="vehicle-desc">{[[v.make, v.model, v.year].filter(Boolean).join(' '), v.color].filter(Boolean).join(' · ') || 'Sin datos de marca'}</p>
          </section>
          <section className="menu-group">
            <h2 className="micro-title">Datos</h2>
            <dl className="data-rows">
              <div>
                <dt>Tipo</dt>
                <dd>{v.vehicle_type_name ?? '—'}</dd>
              </div>
              <div>
                <dt>Marca y modelo</dt>
                <dd>{[v.make, v.model].filter(Boolean).join(' ') || '—'}</dd>
              </div>
              <div>
                <dt>Año</dt>
                <dd className="mono">{v.year ?? '—'}</dd>
              </div>
              <div>
                <dt>Color</dt>
                <dd>{v.color ?? '—'}</dd>
              </div>
              <div>
                <dt>Capacidad</dt>
                <dd>
                  {v.capacity} {v.capacity === 1 ? 'pasajero' : 'pasajeros'}
                </dd>
              </div>
            </dl>
          </section>
          <section className="menu-group">
            <div className="micro-row">
              <h2 className="micro-title">Documentos del vehículo</h2>
              <Link to="/cuenta/documentos" className="micro-link">
                Ver todos
              </Link>
            </div>
            {v.requirements.length === 0 ? (
              <div className="page-pad">
                <Empty>La empresa no exige documentos para el vehículo.</Empty>
              </div>
            ) : (
              <RequirementList items={v.requirements} />
            )}
            <div className="page-pad stack">
              <p className="hint">Los registra la oficina: si algo falta o está por vencer, llévales el documento.</p>
              <DocumentHistory items={v.documents} kind="vehicle" />
            </div>
          </section>
        </>
      ) : (
        <div className="page-pad">
          <Empty title="No tienes vehículo asignado.">Pide a la oficina que te asigne uno para poder recibir servicios.</Empty>
        </div>
      )}
    </SubPage>
  );
}

export function Documents() {
  const docs = useAsync(() => api.get<DocumentsData>('/driver/documents'));
  // the vehicle's documents live here too (DRV-010): one place for everything that can stop offers
  const veh = useAsync(() => api.get<VehicleData | null>('/driver/vehicle'));
  return (
    <SubPage title="Documentos" intro="La oficina revisa cada documento: si algo falta o está por vencer, envíalo o llévalo." flush>
      <Loadable state={docs}>{(data) => <DocumentsBody data={data} vehicle={veh.data ?? null} reload={docs.reload} />}</Loadable>
    </SubPage>
  );
}

function DocumentsBody({ data, vehicle, reload }: { data: DocumentsData; vehicle: VehicleData | null; reload: () => Promise<void> }) {
  const { run, busy } = useAction();
  const { refresh } = useDriver();
  // the types that apply to this driver (active, and for the vehicle type they drive)
  const types = data.documentTypes.filter((t) => data.requirements.driver.some((r) => r.documentTypeId === t.id));
  const empty = { documentTypeId: types[0]?.id ?? '', number: '', expiresAt: '' };
  const [form, setForm] = useState(empty);
  // the form is the exception, not the screen: it opens when the driver has something to register
  const [adding, setAdding] = useState(false);
  const needsExpiry = types.find((t) => t.id === form.documentTypeId)?.requires_expiry;
  const [file, setFile] = useState<File | null>(null);
  const open = (documentTypeId: string) => {
    setForm({ ...empty, documentTypeId });
    setFile(null);
    setAdding(true);
  };
  const requirements = data.requirements.driver;
  if (adding)
    return (
      <div className="page-pad">
        <form
          className="card stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              // the photo goes first; the document then carries its id
              const uploaded = file ? await api.upload<{ id: string }>('/driver/documents/files', await prepareDocumentFile(file)) : null;
              return api.post('/driver/documents', { ...form, expiresAt: form.expiresAt || null, number: form.number.trim() || null, fileId: uploaded?.id ?? null });
            }, 'Documento enviado a revisión').then((r) => {
              if (r === undefined) return;
              setForm(empty);
              setFile(null);
              setAdding(false);
              void reload();
              // the home screen warns about documents from the profile: keep it in step
              void refresh();
            });
          }}
        >
          <h2 className="section-head">Registrar documento</h2>
          <p className="muted small">La oficina lo revisará y verás el resultado aquí.</p>
          <Field label="Tipo">
            <select className="input" value={form.documentTypeId} onChange={(e) => setForm({ ...form, documentTypeId: e.target.value })}>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field name="number" label="Número del documento" optional>
            <input className="input" maxLength={60} autoComplete="off" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
          </Field>
          <Field name="expiresAt" label="Fecha de vencimiento" optional={!needsExpiry}>
            <input className="input" type="date" required={!!needsExpiry} min={todayInput()} value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
          </Field>
          <DocumentFileInput value={file} onChange={setFile} label="Foto del documento" hint="Tómala con buena luz, sin reflejos, que se lea todo. También puedes subir un PDF." />
          <div className="form-actions">
            <button type="button" className="btn lg" onClick={() => setAdding(false)}>
              Volver
            </button>
            <button className="btn primary lg" disabled={busy}>
              {busy ? (file ? 'Subiendo…' : 'Enviando…') : 'Enviar a revisión'}
            </button>
          </div>
        </form>
      </div>
    );
  return (
    <>
      {!data.compliance.driver.valid && (
        <div className="page-pad">
          <Notice tone="warn">Tienes documentos obligatorios faltantes, rechazados o vencidos: no recibirás servicios hasta que estén al día.</Notice>
        </div>
      )}
      <section className="menu-group">
        <h2 className="micro-title">Tuyos</h2>
        {requirements.length === 0 ? (
          <div className="page-pad">
            <Empty>La empresa no exige documentos.</Empty>
          </div>
        ) : (
          <RequirementList items={requirements} onSend={open} />
        )}
      </section>
      {vehicle && vehicle.requirements.length > 0 && (
        <section className="menu-group">
          <h2 className="micro-title">
            Del vehículo <span className="mono">{vehicle.plate}</span>
          </h2>
          <RequirementList items={vehicle.requirements} />
        </section>
      )}
      <div className="page-pad stack">
        {types.length > 0 && (
          <button type="button" className="btn block tall" onClick={() => open(types[0]!.id)}>
            Registrar otro documento
          </button>
        )}
        <DocumentHistory items={data.documents} kind="driver" />
      </div>
    </>
  );
}
